import React, { useState, useEffect, useMemo } from "react";
import { getHrSettings, calculateGleitzeit, getCorrections } from "../services/azureHrService";
import { getAllTeamMembers } from "../services/azureTeamService";
import { getTimekeepingData } from "../services/azureTimekeepingService";
import { getSalaryEntries } from "../services/azureSalaryService";

const GleitzeitDisplay = ({ selectedEmployee }) => {
  const [hrSettings, setHrSettings] = useState(null);
  const [teamMember, setTeamMember] = useState(null);
  const [totalHoursWorked, setTotalHoursWorked] = useState(0);
  const [holidaysTaken, setHolidaysTaken] = useState(0);
  const [holidaysPlanned, setHolidaysPlanned] = useState(0);
  const [pastFerienEntries, setPastFerienEntries] = useState([]);
  const [salaryEntries, setSalaryEntries] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const todayStr = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (selectedEmployee) {
      loadData();
    }
  }, [selectedEmployee]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settings, teamMembers, timekeeping] = await Promise.all([
        getHrSettings(String(currentYear)),
        getAllTeamMembers(),
        getTimekeepingData(),
      ]);

      setHrSettings(settings);

      // Match team member to selected employee
      const member = teamMembers.find(
        (m) => m.employeeUuid === selectedEmployee.uuid ||
               m.name === `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
      );
      setTeamMember(member);

      if (member) {
        // Load corrections for this member (einmal laden, unten fuer
        // relevantCorrections wiederverwenden statt erneut abzufragen)
        let corr = [];
        try {
          corr = await getCorrections(member.teamId, currentYear);
          setCorrections(corr);
        } catch {
          setCorrections([]);
        }

        // Load salary entries for pensum history
        try {
          const salary = await getSalaryEntries(member.teamId);
          setSalaryEntries(salary);
        } catch {
          setSalaryEntries([]);
        }

        // Filter timekeeping for this employee and current year
        const employeeEntries = timekeeping.filter(
          (entry) => {
            const matchesEmployee = entry.employeeUuid === selectedEmployee.uuid ||
              entry.employeeName === `${selectedEmployee.firstName} ${selectedEmployee.lastName}`;
            const matchesYear = entry.date && entry.date.startsWith(String(currentYear));
            return matchesEmployee && matchesYear;
          }
        );

        // Find the latest correction to determine calculation start
        const todayStr = new Date().toISOString().split("T")[0];
        const relevantCorrections = corr.filter((c) => c.date <= todayStr);
        const latestCorrection = relevantCorrections.length > 0
          ? relevantCorrections[relevantCorrections.length - 1]
          : null;

        // Calculate hours worked since last correction (or year start)
        const calcStart = latestCorrection
          ? (() => { const d = new Date(latestCorrection.date); d.setDate(d.getDate() + 1); return d.toISOString().split("T")[0]; })()
          : `${currentYear}-01-01`;

        const entriesSinceCorrection = employeeEntries.filter(
          (entry) => entry.date >= calcStart
        );

        // Ferientage-Zählung:
        //  - Ganztägige Ferien (isFullDay): 1 Tag pro Eintrag
        //  - "Ferien in h" (stundenweise): Summe der Stunden / Tagesarbeitszeit
        //  - Andere Ganztag-Abwesenheiten (Krankheit, Hochzeit, ...) zählen NICHT als Ferien
        const tagesarbeitszeit = settings?.tagesarbeitszeit || 8.24;

        const isFerienHourly = (entry) =>
          (entry.typeName || "").toLowerCase().includes("ferien in h");
        const isFerienFullDay = (entry) => {
          const t = (entry.typeName || "").toLowerCase();
          if (isFerienHourly(entry)) return false;
          return (
            entry.isFullDay &&
            (t.includes("ferien") || t.includes("urlaub") || t.includes("vacation"))
          );
        };

        // Ferien aus totalHoursWorked ausschliessen — Credit erfolgt in calculateGleitzeit.
        let totalMinutes = 0;
        entriesSinceCorrection.forEach((entry) => {
          if (isFerienFullDay(entry) || isFerienHourly(entry)) return;
          totalMinutes += (entry.durationHours || 0) * 60 + (entry.durationMinutes || 0);
        });
        setTotalHoursWorked(totalMinutes / 60);

        const sumHolidayDays = (entries) => {
          const fullDayCount = entries.filter(isFerienFullDay).length;
          const hourlyHours = entries
            .filter(isFerienHourly)
            .reduce(
              (s, e) => s + (e.durationHours || 0) + (e.durationMinutes || 0) / 60,
              0
            );
          return fullDayCount + hourlyHours / tagesarbeitszeit;
        };

        const pastHolidays = entriesSinceCorrection.filter(
          (entry) => entry.date <= todayStr
        );
        const futureHolidays = entriesSinceCorrection.filter(
          (entry) => entry.date > todayStr
        );

        // Nur vergangene Ferien-Einträge an calculateGleitzeit übergeben (für Credit)
        const pastFerien = pastHolidays.filter(
          (e) => isFerienFullDay(e) || isFerienHourly(e)
        );
        setPastFerienEntries(pastFerien);
        setHolidaysTaken(sumHolidayDays(pastHolidays));
        setHolidaysPlanned(sumHolidayDays(futureHolidays));
      }
    } catch (err) {
      console.error("Gleitzeit load error:", err);
    } finally {
      setLoading(false);
    }
  };

  const gleitzeitData = useMemo(() => {
    if (!hrSettings || !teamMember) return null;
    return calculateGleitzeit(
      hrSettings,
      teamMember,
      totalHoursWorked,
      corrections,
      pastFerienEntries,
      salaryEntries
    );
  }, [hrSettings, teamMember, totalHoursWorked, corrections, pastFerienEntries, salaryEntries]);

  const formatDays = (value) => {
    if (!value) return "0";
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  };

  if (loading || !gleitzeitData) return null;

  const gleitzeitValue = parseFloat(gleitzeitData.gleitzeit);
  const isNegative = gleitzeitValue < 0;
  const hasHolidayData = parseFloat(gleitzeitData.holidayEntitlement) > 0;

  return (
    <div className="gleitzeit-display">
      <div className="gleitzeit-item">
        <span className="gleitzeit-label">Tagesarbeitszeit</span>
        <span className="gleitzeit-value">{gleitzeitData.taeglicheArbeitszeit}h</span>
      </div>
      <div className="gleitzeit-separator"></div>
      <div className="gleitzeit-item">
        <span className="gleitzeit-label">Gleitzeit</span>
        <span className={`gleitzeit-value ${isNegative ? "negative" : "positive"}`}>
          {isNegative ? "" : "+"}{gleitzeitData.gleitzeit}h
        </span>
      </div>
      {hasHolidayData && (
        <>
          <div className="gleitzeit-separator"></div>
          <div className="gleitzeit-item">
            <span className="gleitzeit-label">Ferientage {currentYear}</span>
            <span className="gleitzeit-value">
              {formatDays(holidaysTaken)}/{gleitzeitData.holidayEntitlement}
              {holidaysPlanned > 0 && (
                <span className="holidays-planned"> (+{formatDays(holidaysPlanned)})</span>
              )}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default GleitzeitDisplay;
